import { ArrowUpRight, HardHat } from "lucide-react";
import Reveal from "./Reveal";

const PRO_IMG =
  "https://images.unsplash.com/photo-1604277598647-eadc8645952c?q=80&w=1600&auto=format&fit=crop";

const PRO_URL = "https://www.lantixpro.com";

const PRO_FEATURES = [
  "Core discovery feed of live Boston events",
  "Event listings with artist & venue info",
  "Social layer to coordinate your crew",
];

export default function LantixPro() {
  return (
    <section id="pro" className="relative py-24 lg:py-32">
      <div
        className="pointer-events-none absolute left-[-10%] top-1/3 h-[500px] w-[500px] rounded-full bg-brand-deep opacity-40 blur-[170px]"
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div
            className="group relative overflow-hidden rounded-[2.5rem] border border-brand-gold/25 bg-brand-card/80 backdrop-blur-xl transition-colors duration-500 hover:border-brand-gold/50"
            data-testid="pro-section-card"
          >
            <div className="grid lg:grid-cols-2">
              <div className="relative z-10 p-8 sm:p-12 lg:p-16">
                <span className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-gold">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-teal" />
                  The MVP · Live now
                </span>
                <h2 className="mt-6 font-display text-4xl font-extrabold uppercase leading-[1.02] tracking-tight sm:text-5xl">
                  Meet <span className="text-gradient">Lantix Pro.</span>
                </h2>
                <p className="mt-5 max-w-md text-base leading-relaxed text-brand-mist/70">
                  The first live build of the Lantix platform. The MVP behind the movement.
                  Everything you need to discover, list and run local events across Boston, live
                  today.
                </p>
                <ul className="mt-8 space-y-3.5" data-testid="pro-features">
                  {PRO_FEATURES.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-brand-mist/80">
                      <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-brand-teal" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <a
                    href={PRO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="pro-external-link"
                    className="group/link flex items-center gap-2.5 rounded-full bg-brand-teal px-7 py-4 text-sm font-bold text-black shadow-[0_0_32px_rgba(66,246,143,0.4)] transition-all duration-300 hover:shadow-[0_0_52px_rgba(66,246,143,0.7)] hover:brightness-110"
                  >
                    Explore Lantix Pro
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                  </a>
                  <span className="text-sm font-medium text-brand-mist/50">
                    lantixpro.com
                  </span>
                </div>

                <div
                  className="mt-10 rounded-2xl border border-brand-gold/25 bg-brand-gold/5 p-6"
                  data-testid="pro-stagehands-panel"
                >
                  <p className="flex items-center gap-2.5 font-display text-lg font-bold text-brand-paper">
                    <HardHat className="h-5 w-5 text-brand-gold" />
                    Built for stagehands too
                  </p>
                  <ul className="mt-4 space-y-2.5" data-testid="pro-stagehands-benefits">
                    <li className="flex items-start gap-3 text-sm text-brand-mist/80">
                      <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-brand-gold" />
                      Spot every call in Boston with one live feed of local events worth working
                    </li>
                    <li className="flex items-start gap-3 text-sm text-brand-mist/80">
                      <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-brand-gold" />
                      Venue and event info up front, so you walk in knowing the room
                    </li>
                    <li className="flex items-start gap-3 text-sm text-brand-mist/80">
                      <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-brand-gold" />
                      Coordinate your crew: call times and who&apos;s going, in one place
                    </li>
                  </ul>
                </div>
              </div>

              <div className="relative min-h-[300px] lg:min-h-0">
                <img
                  src={PRO_IMG}
                  alt="Performer under a purple spotlight"
                  className="absolute inset-0 h-full w-full object-cover opacity-80 saturate-[0.9] transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-card via-[#1a0640]/30 to-transparent lg:bg-gradient-to-r lg:from-brand-card lg:via-[#1a0640]/20 lg:to-transparent" />
                <div className="absolute inset-0 bg-brand-purple/10 mix-blend-overlay" />
                <span
                  className="pointer-events-none absolute right-6 top-6 rounded-full border border-white/20 bg-black/50 px-3.5 py-1.5 font-brand text-[11px] font-bold tracking-[0.2em] text-brand-paper backdrop-blur-md"
                  data-testid="pro-wordmark-badge"
                >
                  LANTIX PRO
                </span>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
